import { useMemo } from "react";
import { DragDropContext, Droppable, DropResult } from "react-beautiful-dnd";
import { Market, Schedule } from "tm-shared/types";

import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import { setCurrentScheduleList } from "@/store/slices/sv";

import { ScheduleMenuConfig } from "../../RouteEditor.types";
import { ScheduleCard } from "../ScheduleCard";

export interface ScheduleListProps {
    displaySchedules: Schedule[];
    multiSelectMode: boolean;
    selectedSchedules: Schedule[];
    handleSelectSchedule: (item: Schedule) => void;
    handleTap: () => void;
    dayFilter: string;
    scheduleMenuConfig: ScheduleMenuConfig[];
    markets: Record<string, Market>;
}

export const ScheduleList = ({
    displaySchedules,
    multiSelectMode,
    selectedSchedules,
    handleSelectSchedule,
    handleTap,
    scheduleMenuConfig,
    dayFilter,
    markets,
}: ScheduleListProps) => {
    const dispatch = useAppDispatch();

    const { currentScheduleList } = useAppSelector((state) => state.sv);

    const selectedRouteIds = useMemo(
        () => new Set(selectedSchedules.map((r) => r.marketId)),
        [selectedSchedules],
    );

    const handleOnDragEnd = (result: DropResult) => {
        if (!result.destination) return;

        const selectedDay = dayFilter ?? "0";

        if (selectedDay === "0") {
            const reorderedItems = Array.from(currentScheduleList);
            const [movedItem] = reorderedItems.splice(result.source.index, 1);
            reorderedItems.splice(result.destination.index, 0, movedItem);

            const updatedItems = reorderedItems.map((item, index) => ({
                ...item,
                sortOrder: index + 1,
            }));
            dispatch(setCurrentScheduleList(updatedItems));
        } else {
            const filteredItems = [...currentScheduleList]
                .filter((item) => item.days.includes(Number(selectedDay)))
                .sort((a, b) => a.sortOrder - b.sortOrder);

            const reorderedFiltered = Array.from(filteredItems);
            const [movedItem] = reorderedFiltered.splice(
                result.source.index,
                1,
            );
            reorderedFiltered.splice(result.destination.index, 0, movedItem);

            let filterIndex = 0;
            const updatedGlobal = currentScheduleList.map((item) => {
                if (item.days.includes(Number(selectedDay))) {
                    const newItem = reorderedFiltered[filterIndex];
                    filterIndex++;
                    return newItem;
                }
                return item;
            });

            const finalList = updatedGlobal.map((item, index) => ({
                ...item,
                sortOrder: index + 1,
            }));

            dispatch(setCurrentScheduleList(finalList));
        }
    };

    return (
        <DragDropContext onDragEnd={handleOnDragEnd}>
            <Droppable droppableId="droppable">
                {(provided) => (
                    <div
                        {...provided.droppableProps}
                        ref={provided.innerRef}
                        className="block-container"
                    >
                        {displaySchedules.map((item, index) => {
                            const market = markets[item.marketId];
                            const isSelected = selectedRouteIds.has(
                                item.marketId,
                            );

                            return (
                                <ScheduleCard
                                    key={item.marketId}
                                    schedule={item}
                                    index={index}
                                    market={market}
                                    isSelected={isSelected}
                                    multiSelectMode={multiSelectMode}
                                    handleSelectSchedule={handleSelectSchedule}
                                    handleTap={handleTap}
                                    scheduleMenuConfig={scheduleMenuConfig}
                                />
                            );
                        })}
                        {provided.placeholder}
                    </div>
                )}
            </Droppable>
        </DragDropContext>
    );
};
